/**
 * Scaled tarmac ribbon draped along the route fly-through
 * (docs/plans/2026-07-02-route-flythrough-design.md). Purely illustrative
 * scene furniture — the GPX follows real roads/tracks, so painting them
 * onto the terrain helps with orientation, but this has no bearing on any
 * LOS result (hard constraint #1) and no elevation here is ever invented:
 * every vertex comes from the same collider `sampleGround` that backs
 * walk-mode ground lock (hard constraint #6).
 *
 * The pure geometry-building functions below (densifyPolyline,
 * buildCrossSections, resolveRibbonVertices, buildRibbonTriangles) are
 * three.js-free and unit-tested directly in Node. RouteRibbon wraps them
 * in a three.js Mesh and exposes update(sampleGround) so the ribbon can be
 * built incrementally as terrain tiles stream in (design doc "Data flow"
 * step 4): call it on the same tick as TileLoader.updateAround, and any
 * previously-unresolved vertex whose ground is now loaded gets painted in.
 */
import { BufferAttribute, BufferGeometry, DoubleSide, Mesh, MeshStandardMaterial } from 'three';
import type { RoutePolyline } from '../core/route';
import type { GroundSampler } from '../camera/groundLock';
import type { LocalXZ } from '../core/coordinates';

export const RIBBON_WIDTH_M = 4;
export const RIBBON_LIFT_M = 0.15;
export const RIBBON_DENSIFY_SPACING_M = 5;

export interface RibbonCrossSection {
  left: LocalXZ;
  right: LocalXZ;
}

export interface RibbonVertex3 {
  x: number;
  /** metres AOD */
  y: number;
  z: number;
}

export interface ResolvedRibbonVertex {
  left: RibbonVertex3 | null;
  right: RibbonVertex3 | null;
}

/**
 * Resamples a route polyline at approximately `spacingM` intervals. Always
 * includes the exact start and end point so the ribbon reaches both ends
 * of the route.
 */
export function densifyPolyline(polyline: RoutePolyline, spacingM = RIBBON_DENSIFY_SPACING_M): LocalXZ[] {
  const { points, totalLength } = polyline;
  if (points.length <= 1 || totalLength === 0) {
    return points.map((p) => ({ x: p.x, z: p.z }));
  }
  const out: LocalXZ[] = [];
  const steps = Math.max(1, Math.ceil(totalLength / spacingM));
  for (let i = 0; i <= steps; i++) {
    const distance = Math.min(totalLength, (i / steps) * totalLength);
    out.push(pointAtDistanceLocal(polyline, distance));
  }
  return out;
}

/** Local copy of routePlayer's pointAtDistance interpolation (kept dependency-free of camera/). */
function pointAtDistanceLocal(polyline: RoutePolyline, distanceM: number): LocalXZ {
  const { points, cumulativeDistances } = polyline;
  if (distanceM <= cumulativeDistances[0]) return { x: points[0].x, z: points[0].z };
  const lastIdx = points.length - 1;
  if (distanceM >= cumulativeDistances[lastIdx]) return { x: points[lastIdx].x, z: points[lastIdx].z };
  for (let i = 1; i <= lastIdx; i++) {
    if (distanceM <= cumulativeDistances[i]) {
      const segStart = cumulativeDistances[i - 1];
      const segEnd = cumulativeDistances[i];
      const segLen = segEnd - segStart;
      const t = segLen > 0 ? (distanceM - segStart) / segLen : 0;
      const a = points[i - 1];
      const b = points[i];
      return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
    }
  }
  return { x: points[lastIdx].x, z: points[lastIdx].z };
}

/**
 * Builds a perpendicular left/right offset cross-section at every point,
 * `widthM` metres apart, perpendicular (in the horizontal plane) to the
 * local direction of travel. Direction is a central difference against
 * neighbouring points (forward/backward difference at the two ends).
 */
export function buildCrossSections(points: LocalXZ[], widthM = RIBBON_WIDTH_M): RibbonCrossSection[] {
  const halfWidth = widthM / 2;
  return points.map((p, i) => {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    const dx = next.x - prev.x;
    const dz = next.z - prev.z;
    const len = Math.hypot(dx, dz);
    const dirX = len > 1e-9 ? dx / len : 0;
    const dirZ = len > 1e-9 ? dz / len : 1;
    // "Right" of travel direction (dirX, dirZ) in the x-east/z-north plane.
    const rightX = dirZ * halfWidth;
    const rightZ = -dirX * halfWidth;
    return {
      right: { x: p.x + rightX, z: p.z + rightZ },
      left: { x: p.x - rightX, z: p.z - rightZ },
    };
  });
}

/**
 * Resolves each cross-section's left/right offset to a 3D vertex via
 * `sampleGround`, lifted `liftM` above the sampled ground. A side is left
 * null (unresolved) when the sampler reports no ground there — segments
 * touching an unresolved vertex are skipped by buildRibbonTriangles rather
 * than guessing a height (hard constraint #6/#7).
 */
export function resolveRibbonVertices(
  sections: RibbonCrossSection[],
  sampleGround: GroundSampler,
  liftM = RIBBON_LIFT_M,
): ResolvedRibbonVertex[] {
  return sections.map((s) => ({
    left: resolveOne(s.left, sampleGround, liftM),
    right: resolveOne(s.right, sampleGround, liftM),
  }));
}

function resolveOne(p: LocalXZ, sampleGround: GroundSampler, liftM: number): RibbonVertex3 | null {
  const ground = sampleGround(p.x, p.z);
  if (ground === null) return null;
  return { x: p.x, y: ground + liftM, z: p.z };
}

/**
 * Builds a flat position array (triangle list, 3 floats/vertex, 2
 * triangles per consecutive fully-resolved cross-section pair). A segment
 * between cross-section i and i+1 is skipped entirely if either side of
 * either cross-section is unresolved.
 */
export function buildRibbonTriangles(resolved: ResolvedRibbonVertex[]): Float32Array {
  const out: number[] = [];
  for (let i = 0; i < resolved.length - 1; i++) {
    const a = resolved[i];
    const b = resolved[i + 1];
    if (!a.left || !a.right || !b.left || !b.right) continue;
    pushVertex(out, a.left);
    pushVertex(out, a.right);
    pushVertex(out, b.right);

    pushVertex(out, a.left);
    pushVertex(out, b.right);
    pushVertex(out, b.left);
  }
  return new Float32Array(out);
}

function pushVertex(out: number[], v: RibbonVertex3): void {
  out.push(v.x, v.y, v.z);
}

/** The main route ribbon's "drawn tarmac" material. The OSM road-ribbon layer
 * (roadRibbons.ts, issue #138) deliberately uses its OWN dedicated material
 * (per-vertex colours for the service/tarmac split) rather than this one, so
 * the route ribbon's colour is never tinted — this stays a single-colour
 * material for the route alone. */
export const RIBBON_MATERIAL = new MeshStandardMaterial({
  color: 0x2a2d33,
  roughness: 0.9,
  metalness: 0.0,
  side: DoubleSide,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -1,
});

/**
 * Three.js wrapper: builds and incrementally refines the tarmac ribbon
 * mesh as terrain tiles stream in. Construct once with the loaded route
 * polyline, add `.mesh` to the scene, and call `.update(sampleGround)`
 * on the same cadence as tile loading (design doc "Data flow" step 4).
 */
export class RouteRibbon {
  readonly mesh: Mesh;
  private readonly sections: RibbonCrossSection[];
  private resolved: ResolvedRibbonVertex[];

  constructor(polyline: RoutePolyline, widthM = RIBBON_WIDTH_M, spacingM = RIBBON_DENSIFY_SPACING_M) {
    const densified = densifyPolyline(polyline, spacingM);
    this.sections = buildCrossSections(densified, widthM);
    this.resolved = this.sections.map(() => ({ left: null, right: null }));

    const geometry = new BufferGeometry();
    this.mesh = new Mesh(geometry, RIBBON_MATERIAL);
    this.mesh.name = 'route-ribbon';
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
  }

  /** Resolved-vertex count, exposed for tests/diagnostics. */
  get unresolvedCount(): number {
    return this.resolved.filter((v) => !v.left || !v.right).length;
  }

  /**
   * Re-samples every not-yet-resolved cross-section against the current
   * ground sampler and rebuilds the mesh geometry if anything newly
   * resolved. Safe to call every frame/tile-update tick; it is a no-op
   * once every cross-section is resolved.
   */
  update(sampleGround: GroundSampler): void {
    let changed = false;
    for (let i = 0; i < this.sections.length; i++) {
      const v = this.resolved[i];
      if (!v.left) {
        const l = resolveOne(this.sections[i].left, sampleGround, RIBBON_LIFT_M);
        if (l) {
          v.left = l;
          changed = true;
        }
      }
      if (!v.right) {
        const r = resolveOne(this.sections[i].right, sampleGround, RIBBON_LIFT_M);
        if (r) {
          v.right = r;
          changed = true;
        }
      }
    }
    if (!changed) return;

    const positions = buildRibbonTriangles(this.resolved);
    const geometry = this.mesh.geometry;
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
  }
}
